export const NP_MAP_TAG = 'np-map';
export const NP_MAP_OPEN_TAG = `<${NP_MAP_TAG}>`;
export const NP_MAP_CLOSE_TAG = `</${NP_MAP_TAG}>`;

export type MapMode = 'place' | 'view' | 'directions' | 'streetview' | 'search';
export type MapType = 'roadmap' | 'satellite';
export type MapTravelMode =
  | 'driving'
  | 'walking'
  | 'bicycling'
  | 'transit'
  | 'flying';
export type MapAvoidOption = 'tolls' | 'highways' | 'ferries';
export type MapUnits = 'metric' | 'imperial';

export interface MapCoordinates {
  lat: number;
  lng: number;
}

export interface MapBlockData {
  mode: MapMode;
  height: number;
  zoom?: number;
  mapType?: MapType;
  language?: string;
  query?: string;
  center?: MapCoordinates;
  origin?: string;
  destination?: string;
  waypoints?: string[];
  travelMode?: MapTravelMode;
  avoid?: MapAvoidOption[];
  units?: MapUnits;
  location?: string;
  heading?: number;
  pitch?: number;
  fov?: number;
}

export interface ParsedMarkdownMapConfig {
  data: MapBlockData;
  rawConfig: string;
  parseError: string | null;
}

export interface MarkdownMapEditValues {
  mode: MapMode;
  height: string;
  zoom: string;
  mapType: MapType;
  language: string;
  query: string;
  centerLat: string;
  centerLng: string;
  origin: string;
  destination: string;
  waypoints: string;
  travelMode: string;
  avoid: string;
  units: string;
  location: string;
  heading: string;
  pitch: string;
  fov: string;
}

export interface MarkdownMapEditRequest {
  mode: 'edit' | 'insert';
  initialValues: MarkdownMapEditValues;
  onCommit: (values: MarkdownMapEditValues) => void | Promise<void>;
}

const MAP_MODES = new Set<MapMode>([
  'place',
  'view',
  'directions',
  'streetview',
  'search',
]);
const MAP_TYPES = new Set<MapType>(['roadmap', 'satellite']);
const MAP_TRAVEL_MODES = new Set<MapTravelMode>([
  'driving',
  'walking',
  'bicycling',
  'transit',
  'flying',
]);
const MAP_AVOID_OPTIONS = new Set<MapAvoidOption>([
  'tolls',
  'highways',
  'ferries',
]);
const MAP_UNITS = new Set<MapUnits>(['metric', 'imperial']);

export const DEFAULT_MAP_HEIGHT = 320;
export const MIN_MAP_HEIGHT = 200;
export const MAX_MAP_HEIGHT = 640;

function normalizeRawConfig(value: string): string {
  return value.replace(/\r\n/g, '\n').trim();
}

function minifyJsonConfig(value: unknown): string {
  return JSON.stringify(value);
}

function normalizeString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function normalizeOptionalString(value: unknown): string | undefined {
  const trimmed = normalizeString(value);
  return trimmed || undefined;
}

function normalizeMapMode(value: unknown): MapMode {
  return MAP_MODES.has(value as MapMode) ? (value as MapMode) : 'place';
}

function normalizeMapType(value: unknown): MapType | undefined {
  return MAP_TYPES.has(value as MapType) ? (value as MapType) : undefined;
}

function normalizeMapTravelMode(value: unknown): MapTravelMode | undefined {
  return MAP_TRAVEL_MODES.has(value as MapTravelMode)
    ? (value as MapTravelMode)
    : undefined;
}

function normalizeMapUnits(value: unknown): MapUnits | undefined {
  return MAP_UNITS.has(value as MapUnits) ? (value as MapUnits) : undefined;
}

function normalizeFiniteNumber(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }

  return undefined;
}

function clampInteger(
  value: number | undefined,
  min: number,
  max: number,
): number | undefined {
  if (value == null) {
    return undefined;
  }

  return Math.min(max, Math.max(min, Math.round(value)));
}

function normalizeHeight(value: unknown): number {
  return (
    clampInteger(
      normalizeFiniteNumber(value),
      MIN_MAP_HEIGHT,
      MAX_MAP_HEIGHT,
    ) ?? DEFAULT_MAP_HEIGHT
  );
}

function normalizeZoom(value: unknown): number | undefined {
  return clampInteger(normalizeFiniteNumber(value), 0, 21);
}

function normalizePitch(value: unknown): number | undefined {
  const parsed = normalizeFiniteNumber(value);
  if (parsed == null) return undefined;

  return Math.min(90, Math.max(-90, parsed));
}

function normalizeFov(value: unknown): number | undefined {
  const parsed = normalizeFiniteNumber(value);
  if (parsed == null) return undefined;

  return Math.min(100, Math.max(10, parsed));
}

function normalizeHeading(value: unknown): number | undefined {
  return normalizeFiniteNumber(value);
}

function normalizeCoordinates(value: unknown): MapCoordinates | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return undefined;
  }

  const rec = value as Record<string, unknown>;
  const lat = normalizeFiniteNumber(rec.lat);
  const lng = normalizeFiniteNumber(rec.lng);
  if (lat == null || lng == null) {
    return undefined;
  }

  return { lat, lng };
}

function normalizeStringArray(value: unknown): string[] | undefined {
  if (!Array.isArray(value)) {
    return undefined;
  }

  const items = value
    .map((item) => normalizeString(item))
    .filter((item) => item.length > 0);
  return items.length > 0 ? items : undefined;
}

function normalizeAvoid(value: unknown): MapAvoidOption[] | undefined {
  const values = Array.isArray(value)
    ? value
    : typeof value === 'string'
      ? value.split('|')
      : [];
  const items = values
    .map((item) => normalizeString(item))
    .filter((item): item is MapAvoidOption =>
      MAP_AVOID_OPTIONS.has(item as MapAvoidOption),
    );
  return items.length > 0 ? Array.from(new Set(items)) : undefined;
}

function createBaseMapBlockData(input: unknown): MapBlockData {
  const rec =
    input && typeof input === 'object'
      ? (input as Record<string, unknown>)
      : {};

  return {
    mode: normalizeMapMode(rec.mode),
    height: normalizeHeight(rec.height),
    zoom: normalizeZoom(rec.zoom),
    mapType: normalizeMapType(rec.mapType ?? rec.maptype),
    language: normalizeOptionalString(rec.language),
    query: normalizeOptionalString(rec.query ?? rec.q),
    center: normalizeCoordinates(rec.center),
    origin: normalizeOptionalString(rec.origin),
    destination: normalizeOptionalString(rec.destination),
    waypoints: normalizeStringArray(rec.waypoints),
    travelMode: normalizeMapTravelMode(rec.travelMode ?? rec.modeOfTravel),
    avoid: normalizeAvoid(rec.avoid),
    units: normalizeMapUnits(rec.units),
    location: normalizeOptionalString(rec.location),
    heading: normalizeHeading(rec.heading),
    pitch: normalizePitch(rec.pitch),
    fov: normalizeFov(rec.fov),
  };
}

export function normalizeMapBlockData(input: unknown): MapBlockData {
  const base = createBaseMapBlockData(input);

  switch (base.mode) {
    case 'place':
    case 'search':
      return {
        mode: base.mode,
        height: base.height,
        query: base.query,
        center: base.center,
        zoom: base.zoom,
        mapType: base.mapType,
        language: base.language,
      };
    case 'view':
      return {
        mode: 'view',
        height: base.height,
        center: base.center,
        zoom: base.zoom,
        mapType: base.mapType,
        language: base.language,
      };
    case 'directions':
      return {
        mode: 'directions',
        height: base.height,
        origin: base.origin,
        destination: base.destination,
        waypoints: base.waypoints,
        travelMode: base.travelMode,
        avoid: base.avoid,
        units: base.units,
        center: base.center,
        zoom: base.zoom,
        mapType: base.mapType,
        language: base.language,
      };
    case 'streetview':
      return {
        mode: 'streetview',
        height: base.height,
        location: base.location,
        heading: base.heading,
        pitch: base.pitch,
        fov: base.fov,
        center: base.center,
        zoom: base.zoom,
        mapType: base.mapType,
        language: base.language,
      };
  }
}

function mapDataToSerializableConfig(
  data: MapBlockData,
): Record<string, unknown> {
  const normalized = normalizeMapBlockData(data);
  const serialized: Record<string, unknown> = {
    mode: normalized.mode,
    height: normalized.height,
  };

  if (normalized.zoom != null) serialized.zoom = normalized.zoom;

  if (normalized.mapType && normalized.mapType !== 'roadmap') {
    serialized.mapType = normalized.mapType;
  }

  if (normalized.language) serialized.language = normalized.language;

  if (normalized.center) serialized.center = normalized.center;

  switch (normalized.mode) {
    case 'place':
    case 'search':
      if (normalized.query) serialized.query = normalized.query;

      break;
    case 'view':
      break;
    case 'directions':
      if (normalized.origin) serialized.origin = normalized.origin;

      if (normalized.destination)
        serialized.destination = normalized.destination;

      if (normalized.waypoints?.length)
        serialized.waypoints = normalized.waypoints;

      if (normalized.travelMode) serialized.travelMode = normalized.travelMode;

      if (normalized.avoid?.length) serialized.avoid = normalized.avoid;

      if (normalized.units) serialized.units = normalized.units;

      break;
    case 'streetview':
      if (normalized.location) serialized.location = normalized.location;

      if (normalized.heading != null) serialized.heading = normalized.heading;

      if (normalized.pitch != null) serialized.pitch = normalized.pitch;

      if (normalized.fov != null) serialized.fov = normalized.fov;

      break;
  }

  return serialized;
}

export function stringifyMarkdownMapConfig(data: MapBlockData): string {
  return JSON.stringify(mapDataToSerializableConfig(data), null, 2);
}

export function parseMarkdownMapConfig(
  rawConfig: string,
): ParsedMarkdownMapConfig {
  const normalizedRaw = normalizeRawConfig(rawConfig);

  if (!normalizedRaw) {
    return {
      data: normalizeMapBlockData({}),
      rawConfig: normalizedRaw,
      parseError: null,
    };
  }

  try {
    return {
      data: normalizeMapBlockData(JSON.parse(normalizedRaw) as unknown),
      rawConfig: normalizedRaw,
      parseError: null,
    };
  } catch (error) {
    return {
      data: normalizeMapBlockData({}),
      rawConfig: normalizedRaw,
      parseError:
        error instanceof Error ? error.message : 'Could not parse map JSON',
    };
  }
}

export function serializeMarkdownMapInnerConfig(params: {
  data: MapBlockData;
  rawConfig?: string;
  parseError?: string | null;
}): string {
  const normalizedRaw = normalizeRawConfig(params.rawConfig ?? '');
  if (params.parseError && normalizedRaw) {
    return normalizedRaw;
  }

  if (normalizedRaw) {
    try {
      return minifyJsonConfig(JSON.parse(normalizedRaw) as unknown);
    } catch {
      return normalizedRaw;
    }
  }

  return minifyJsonConfig(mapDataToSerializableConfig(params.data));
}

export function serializeMarkdownMapBlock(params: {
  data: MapBlockData;
  rawConfig?: string;
  parseError?: string | null;
}): string {
  return `${NP_MAP_OPEN_TAG}${serializeMarkdownMapInnerConfig(params)}${NP_MAP_CLOSE_TAG}`;
}

export function createDefaultMarkdownMapValue(
  initialValues?: Partial<MarkdownMapEditValues>,
): string {
  return serializeMarkdownMapBlock({
    data: markdownMapEditValuesToBlockData({
      ...createDefaultMarkdownMapEditValues(),
      ...initialValues,
    }),
  });
}

function toEditNumber(value: number | undefined): string {
  return value == null ? '' : String(value);
}

export function mapBlockDataToEditValues(
  data: MapBlockData,
): MarkdownMapEditValues {
  const normalized = normalizeMapBlockData(data);
  return {
    mode: normalized.mode,
    height: String(normalized.height),
    zoom: toEditNumber(normalized.zoom),
    mapType: normalized.mapType ?? 'roadmap',
    language: normalized.language ?? '',
    query: normalized.query ?? '',
    centerLat: toEditNumber(normalized.center?.lat),
    centerLng: toEditNumber(normalized.center?.lng),
    origin: normalized.origin ?? '',
    destination: normalized.destination ?? '',
    waypoints: normalized.waypoints?.join('\n') ?? '',
    travelMode: normalized.travelMode ?? 'driving',
    avoid: normalized.avoid?.join(', ') ?? '',
    units: normalized.units ?? 'metric',
    location: normalized.location ?? '',
    heading: toEditNumber(normalized.heading),
    pitch: toEditNumber(normalized.pitch),
    fov: toEditNumber(normalized.fov),
  };
}

export function createDefaultMarkdownMapEditValues(): MarkdownMapEditValues {
  return {
    mode: 'place',
    height: String(DEFAULT_MAP_HEIGHT),
    zoom: '',
    mapType: 'roadmap',
    language: '',
    query: '',
    centerLat: '',
    centerLng: '',
    origin: '',
    destination: '',
    waypoints: '',
    travelMode: 'driving',
    avoid: '',
    units: 'metric',
    location: '',
    heading: '',
    pitch: '',
    fov: '',
  };
}

function parseOptionalCoordinatePair(
  latValue: string,
  lngValue: string,
): MapCoordinates | undefined {
  if (!latValue.trim() && !lngValue.trim()) {
    return undefined;
  }

  const lat = normalizeFiniteNumber(latValue);
  const lng = normalizeFiniteNumber(lngValue);
  if (lat == null || lng == null) {
    return undefined;
  }

  return { lat, lng };
}

function parseOptionalLineList(value: string): string[] | undefined {
  const items = value
    .split(/\n|\|/)
    .map((item) => item.trim())
    .filter(Boolean);
  return items.length > 0 ? items : undefined;
}

function parseOptionalAvoidList(value: string): string[] | undefined {
  const items = value
    .split(/[|,\n]/)
    .map((item) => item.trim())
    .filter(Boolean);
  return items.length > 0 ? items : undefined;
}

export function markdownMapEditValuesToBlockData(
  values: MarkdownMapEditValues,
): MapBlockData {
  return normalizeMapBlockData({
    mode: values.mode,
    height: values.height,
    zoom: values.zoom,
    mapType: values.mapType,
    language: values.language,
    query: values.query,
    center: parseOptionalCoordinatePair(values.centerLat, values.centerLng),
    origin: values.origin,
    destination: values.destination,
    waypoints: parseOptionalLineList(values.waypoints),
    travelMode: values.travelMode,
    avoid: parseOptionalAvoidList(values.avoid),
    units: values.units,
    location: values.location,
    heading: values.heading,
    pitch: values.pitch,
    fov: values.fov,
  });
}

function stringifyCoordinates(coords: MapCoordinates): string {
  return `${coords.lat},${coords.lng}`;
}

export function getMarkdownMapValidationMessage(
  data: MapBlockData,
): string | null {
  const normalized = normalizeMapBlockData(data);
  switch (normalized.mode) {
    case 'place':
      return normalized.query ? null : 'Enter a place or address query.';
    case 'search':
      return normalized.query ? null : 'Enter a search query.';
    case 'view':
      return normalized.center
        ? null
        : 'Enter center latitude and longitude for view mode.';
    case 'directions':
      if (!normalized.origin) return 'Enter a directions origin.';

      if (!normalized.destination) return 'Enter a directions destination.';

      return null;
    case 'streetview':
      return normalized.location
        ? null
        : 'Enter a street view location or coordinates.';
  }
}

export function buildGoogleMapsEmbedUrl(
  data: MapBlockData,
  apiKey: string,
): string | null {
  const trimmedKey = apiKey.trim();
  if (!trimmedKey) {
    return null;
  }

  const normalized = normalizeMapBlockData(data);
  if (getMarkdownMapValidationMessage(normalized)) {
    return null;
  }

  const url = new URL(
    `https://www.google.com/maps/embed/v1/${normalized.mode}`,
  );
  url.searchParams.set('key', trimmedKey);

  switch (normalized.mode) {
    case 'place':
    case 'search':
      url.searchParams.set('q', normalized.query ?? '');
      break;
    case 'view':
      url.searchParams.set('center', stringifyCoordinates(normalized.center!));
      break;
    case 'directions':
      url.searchParams.set('origin', normalized.origin ?? '');
      url.searchParams.set('destination', normalized.destination ?? '');
      if (normalized.waypoints?.length) {
        url.searchParams.set('waypoints', normalized.waypoints.join('|'));
      }

      if (normalized.travelMode) {
        url.searchParams.set('mode', normalized.travelMode);
      }

      if (normalized.avoid?.length) {
        url.searchParams.set('avoid', normalized.avoid.join('|'));
      }

      if (normalized.units) {
        url.searchParams.set('units', normalized.units);
      }

      break;
    case 'streetview':
      url.searchParams.set('location', normalized.location ?? '');
      if (normalized.heading != null) {
        url.searchParams.set('heading', String(normalized.heading));
      }

      if (normalized.pitch != null) {
        url.searchParams.set('pitch', String(normalized.pitch));
      }

      if (normalized.fov != null) {
        url.searchParams.set('fov', String(normalized.fov));
      }

      break;
  }

  if (normalized.center) {
    url.searchParams.set('center', stringifyCoordinates(normalized.center));
  }

  if (normalized.zoom != null) {
    url.searchParams.set('zoom', String(normalized.zoom));
  }

  if (normalized.mapType) {
    url.searchParams.set('maptype', normalized.mapType);
  }

  if (normalized.language) {
    url.searchParams.set('language', normalized.language);
  }

  return url.toString();
}

export function describeMarkdownMap(data: MapBlockData): string {
  const normalized = normalizeMapBlockData(data);
  switch (normalized.mode) {
    case 'place':
      return normalized.query ? `Place: ${normalized.query}` : 'Place map';
    case 'search':
      return normalized.query ? `Search: ${normalized.query}` : 'Search map';
    case 'view':
      return normalized.center
        ? `View: ${stringifyCoordinates(normalized.center)}`
        : 'View map';
    case 'directions':
      if (normalized.origin && normalized.destination) {
        return `${normalized.origin} → ${normalized.destination}`;
      }

      return 'Directions map';
    case 'streetview':
      return normalized.location
        ? `Street View: ${normalized.location}`
        : 'Street View';
  }
}
